import { useEffect, useRef } from "react";
import { bigDealAmount, coinRainAmount, recordMinAmount } from "../constants";
import { readRecord, writeRecord } from "../utils/saleRecord";

// Kijkt of er tussen twee keer ophalen iets is bijgekomen en vertaalt dat
// naar animaties. De eerste meting telt niet mee: dat is gewoon de stand
// zoals hij was, niet iets wat zojuist gebeurde.

function snapshot(data) {
  const totals = new Map();
  const order = [];

  (data.rankList || []).forEach((user) => {
    if (!user?.name) return;
    totals.set(user.name, user.totalPrice || 0);
    order.push(user.name);
  });

  const target = data.teamTarget?.amount || 0;
  const percent = target
    ? Math.min(100, Math.round(((data.scoredTowardsTarget || 0) / target) * 100))
    : 0;

  return { totals, order, amount: data.amount || 0, percent };
}

// Geeft terug wat er moet gebeuren, als lijst van [animatie, gegevens].
// record: de grootste losse verkoop tot nu toe, of null als we die (nog)
// niet kennen. Zonder record geen vuurwerk - de eerste verkoop die we zien
// is niet per se een record, we hebben alleen de geschiedenis niet.
function toEvents(before, now, record) {
  const events = [];

  // Het team tikt de 100% aan: dat overstemt de rest, dus verder niets.
  // Confetti erbij voor het feestje.
  if (now.percent >= 100 && before.percent < 100) {
    return [
      ["target-hit", { amount: now.amount }],
      ["confetti-burst", {}],
    ];
  }

  // Wie is erbij gekomen of heeft meer op de teller?
  const sales = [];
  now.totals.forEach((price, name) => {
    const was = before.totals.get(name) ?? 0;
    if (price > was) sales.push({ name, amount: price - was });
  });

  // Grootste deal eerst, zodat die de banner krijgt als er meerdere zijn.
  sales.sort((a, b) => b.amount - a.amount);

  sales.forEach((sale) => {
    events.push(["sale-toast", sale]);
    events.push(["row-flash", { name: sale.name }]);
    if (sale.amount >= coinRainAmount) events.push(["coin-rain", {}]);
    if (sale.amount >= bigDealAmount) events.push(["gem-rain", {}]);
  });

  // Gaat de grootste van deze ronde over het record heen? Dan het vuurwerk,
  // met het oude record erbij zodat je ziet wát er gebroken is.
  const biggest = sales[0];
  if (
    biggest &&
    record &&
    biggest.amount > record.amount &&
    biggest.amount >= recordMinAmount
  ) {
    events.push([
      "fireworks",
      { name: biggest.name, amount: biggest.amount, previous: record.amount },
    ]);
  }

  // De grootste stijger in de ranglijst krijgt zijn eigen kaartje.
  let climber = null;
  now.order.forEach((name, index) => {
    const was = before.order.indexOf(name);
    if (was < 0 || was <= index) return;
    const jump = was - index;
    if (!climber || jump > climber.jump) {
      climber = { name, position: index + 1, from: was + 1, jump };
    }
  });

  if (climber) {
    events.push(["rank-up", climber]);
    if (climber.position <= 3) events.push(["podium-bounce", {}]);
  }

  // Er is geld bij gekomen maar niemand sprong eruit: dan alleen de meter.
  if (events.length === 0 && now.amount > before.amount) {
    events.push(["gauge-pulse", {}]);
  } else if (events.length > 0) {
    events.unshift(["gauge-pulse", {}]);
  }

  return events;
}

// Dezelfde vergelijking, maar los te gebruiken: het animatielab past een
// verzonnen verkoop toe op zijn cijfers en vraagt hier wat er dan zou
// gebeuren. Zo test je in het lab precies wat er live draait.
export function diffEvents(beforeData, nowData, record = readRecord()) {
  return toEvents(snapshot(beforeData), snapshot(nowData), record);
}

// De grootste losse verkoop van deze ronde, of null.
function biggestSale(before, now) {
  let best = null;
  now.totals.forEach((price, name) => {
    const was = before.totals.get(name) ?? 0;
    const amount = price - was;
    if (amount > 0 && (!best || amount > best.amount)) best = { name, amount };
  });
  return best;
}

// scope: verandert die (andere maand), dan begint het vergelijken opnieuw.
// Zonder dat zou het overschakelen naar een andere maand een regen aan
// "nieuwe verkopen" opleveren, terwijl er niets is gebeurd.
export function useSaleEvents(data, play, scope) {
  const previous = useRef(null);
  const scopeRef = useRef(scope);
  const timers = useRef([]);
  // Het record loopt dóór over maanden heen, dus die zetten we niet terug
  // als de scope wisselt.
  const record = useRef(readRecord());

  useEffect(() => {
    if (scopeRef.current !== scope) {
      scopeRef.current = scope;
      previous.current = null;
    }

    if (!data) {
      previous.current = null;
      return undefined;
    }

    const now = snapshot(data);
    const before = previous.current;
    previous.current = now;
    if (!before) return undefined;

    const events = toEvents(before, now, record.current);

    // Record bijwerken, ook als er geen animatie bij hoort: kennen we er nog
    // geen, dan is deze verkoop vanaf nu de lat.
    const biggest = biggestSale(before, now);
    if (biggest && biggest.amount > (record.current?.amount ?? 0)) {
      record.current = { ...biggest, at: new Date().toISOString() };
      writeRecord(record.current);
    }

    if (events.length === 0) return undefined;

    // Niet alles tegelijk over elkaar heen: elk volgend seintje wacht even.
    events.forEach(([id, payload], index) => {
      timers.current.push(
        setTimeout(() => play(id, payload), index * 700),
      );
    });

    return undefined;
  }, [data, play, scope]);

  useEffect(
    () => () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    },
    [],
  );
}
